import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync user profile from our backend
  const syncProfile = useCallback(async (session) => {
    if (!session) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      setUser(res.data);
    } catch {
      // User not yet synced - that's ok, they'll sync on register
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncProfile(session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        } else if (session) {
          await syncProfile(session);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [syncProfile]);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Sync profile from backend
    try {
      const res = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${data.session.access_token}` }
      });
      setUser(res.data);
      return res.data;
    } catch {
      // User exists in Supabase but not yet synced to our backend
      throw new Error("Profil non synchronisé. Veuillez vous inscrire d'abord.");
    }
  }, []);

  const register = useCallback(async ({ email, password, first_name, last_name, phone, role }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { first_name, last_name }
      }
    });
    if (error) throw error;

    // If no session (email confirmation required)
    if (!data.session) {
      return { needsConfirmation: true };
    }

    // Sync profile to our backend
    const res = await api.post('/auth/sync',
      { first_name, last_name, phone: phone || '', role: role || 'caregiver' },
      { headers: { Authorization: `Bearer ${data.session.access_token}` } }
    );
    setUser(res.data);
    return res.data;
  }, []);

  const updateProfile = useCallback(async ({ first_name, last_name, phone }) => {
    const res = await api.put('/auth/me', { first_name, last_name, phone: phone || '' });
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_ops';
  const isPartner = user?.role === 'partner';
  const isCaregiver = user?.role === 'caregiver';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, isAdmin, isPartner, isCaregiver }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
